// frontend/src/hooks/useCancelOrder.ts
import { useCallback, useState } from 'react'
import {
  useAccount,
  useConfig,
  useSwitchChain,
  useWalletClient,
} from 'wagmi'
import { getPublicClient } from '@wagmi/core'
import { useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { api as apiClient } from '../lib/api'
import { wagmiChain } from '../lib/p2p/tokens'
import { humanizeP2PError } from '../lib/p2p/errors'

export function useCancelOrder() {
  const { address } = useAccount()
  const { switchChainAsync } = useSwitchChain()
  const { data: walletClient } = useWalletClient()
  const config = useConfig()
  const queryClient = useQueryClient()

  const [pendingId, setPendingId] = useState<string | null>(null)

  const cancel = useCallback(
    async (orderId: string) => {
      if (!address || !walletClient) {
        toast.error('Wallet no conectada')
        return { ok: false as const }
      }
      setPendingId(orderId)
      try {
        const calldata = await apiClient.cancelP2POrderCalldata(orderId)
        const targetChain = wagmiChain(calldata.chain)
        const publicClient = getPublicClient(config, { chainId: targetChain.id })
        if (!publicClient) throw new Error(`No public client for chain ${calldata.chain}`)

        if (walletClient.chain?.id !== targetChain.id) {
          await switchChainAsync({ chainId: targetChain.id })
        }
        const hash = await walletClient.sendTransaction({
          to: calldata.to,
          data: calldata.data,
          value: 0n,
          chain: targetChain,
        })
        const receipt = await publicClient.waitForTransactionReceipt({ hash })
        if (receipt.status !== 'success') {
          throw new Error('La transacción de cancelar se revirtió on-chain')
        }
        toast.success('Orden cancelada')
        queryClient.invalidateQueries({ queryKey: ['p2p', 'orders', 'me'] })
        queryClient.invalidateQueries({ queryKey: ['p2p', 'orderbook'] })
        return { ok: true as const, hash }
      } catch (err) {
        const msg = humanizeP2PError(err)
        toast.error(msg)
        return { ok: false as const, error: msg }
      } finally {
        setPendingId(null)
      }
    },
    [address, walletClient, config, switchChainAsync, queryClient],
  )

  return { cancel, pendingId }
}
